import React, { useMemo } from 'react';
import { Card } from 'react-bootstrap';
import { Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  ChartData,
  ScatterDataPoint,
} from 'chart.js';
import { SimulationConfig, Donation, Donor, Project } from '../models/SimulationModels';
import { SimulationEngine } from '../SimulationEngine';
import { v4 as uuidv4 } from 'uuid';

ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

interface DonationGraphProps {
  donations: Array<{
    amount: number;
    matchingGenerated: number;
  }>;
  config: SimulationConfig;
  selectedProject: Project | null;
  allDonations: Donation[];
  allDonors: Donor[];
}

const DonationGraph: React.FC<DonationGraphProps> = ({ 
  donations, 
  config, 
  selectedProject, 
  allDonations, 
  allDonors 
}) => {
  // Calculate theoretical points using the exact same calculation as QFRoundPage.tsx
  const { theoreticalPoints, linearPoints } = useMemo(() => {
    // If no project is selected, return empty arrays
    if (!selectedProject) {
      return { theoreticalPoints: [], linearPoints: [] };
    }

    // Get existing donations for this project
    const projectDonations = allDonations.filter(d => d.projectId === selectedProject.id);
    
    // Calculate matching impact across a range of donation amounts
    const maxAmount = 200;
    const stepSize = 5; // 40 points for smoother curve
    const points: Array<{x: number, y: number}> = [{x: 0, y: 0}]; // Start at 0
    
    // Create a temporary donor for simulation
    const tempDonor = {
      id: 'temp-donor-for-graph',
      name: 'Graph User',
      verificationType: 'id' as 'id' | 'email' | 'phone' | 'none',
      firstTimeContributor: false,
    };
    
    // Create a temporary engine to calculate the impact - EXACTLY like QFRoundPage
    const tempEngine = new SimulationEngine(config);
    tempEngine.setProjects([selectedProject]);
    tempEngine.setDonors([...allDonors, tempDonor]);
    
    // Run simulation with current donations - BASE STATE
    tempEngine.setDonations(projectDonations);
    const beforeResult = tempEngine.simulate();
    const projectBefore = beforeResult.projects.find(p => p.projectId === selectedProject.id);
    const baseMatchingAmount = projectBefore?.matchingAmount || 0;
    
    for (let amount = stepSize; amount <= maxAmount; amount += stepSize) {
      // Create a test donation with this amount - EXACTLY like QFRoundPage
      const tempDonation = {
        id: `temp-donation-${amount}`,
        projectId: selectedProject.id,
        donorId: tempDonor.id,
        amount,
        timestamp: new Date(),
        ipAddress: '127.0.0.1',
        deviceId: 'browser-device',
        earlyBird: false
      };
      
      // Create a new engine for each calculation to ensure clean state
      const testEngine = new SimulationEngine(config);
      testEngine.setProjects([selectedProject]);
      testEngine.setDonors([...allDonors, tempDonor]);
      
      // Run simulation with all existing donations PLUS the new donation
      testEngine.setDonations([...projectDonations, tempDonation]);
      const afterResult = testEngine.simulate();
      const projectAfter = afterResult.projects.find(p => p.projectId === selectedProject.id);
      
      if (projectAfter) {
        // Calculate the difference in matching - EXACTLY like QFRoundPage
        const matchingImpact = projectAfter.matchingAmount - baseMatchingAmount;
        
        points.push({
          x: amount,
          y: matchingImpact
        });
      }
    }
    
    // Create linear matching points for comparison
    // Use a fixed percentage of the donation amount
    const linearPercent = 0.5; // 50% matching for linear comparison
    const linear = Array.from({ length: Math.floor(maxAmount / stepSize) + 1 }, (_, i) => {
      const amount = i * stepSize;
      return {
        x: amount,
        y: amount * linearPercent
      };
    });
    
    return {
      theoreticalPoints: points,
      linearPoints: linear
    };
  }, [config, selectedProject, allDonations, allDonors]);

  // Prepare data for Chart.js
  const data: ChartData<'scatter', ScatterDataPoint[]> = {
    datasets: [
      {
        type: 'scatter' as const,
        label: 'Actual Donations',
        data: donations.map((donation) => ({
          x: donation.amount,
          y: donation.matchingGenerated,
        })).filter(point => point.x <= 200), // Only include donations <= 200
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
      },
      {
        type: 'scatter' as const,
        label: `${getFormulaName(config.formulaType)} Current Impact`,
        data: theoreticalPoints,
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderDash: [5, 5],
        borderWidth: 2,
        pointRadius: 0,
        showLine: true, // This makes it display as a line
        fill: false,
      },
      {
        type: 'scatter' as const,
        label: 'Linear Matching (1:1)',
        data: linearPoints,
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderDash: [2, 2],
        borderWidth: 2,
        pointRadius: 0,
        showLine: true,
        fill: false,
      },
    ],
  };

  const options = {
    scales: {
      x: {
        type: 'linear' as const,
        position: 'bottom' as const,
        min: 0,
        max: 200,
        title: {
          display: true,
          text: 'Donation Amount (€)',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Additional Matching (€)',
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context: any) => {
            if (!context.dataset.showLine) {
              return `Actual: Donation €${context.parsed.x.toLocaleString()}, Match €${context.parsed.y.toLocaleString()}`;
            } else if (context.dataset.label.includes('Linear')) {
              return `Linear: Donation €${context.parsed.x.toLocaleString()}, Match €${context.parsed.y.toLocaleString()}`;
            }
            return `${getFormulaName(config.formulaType)}: Donation €${context.parsed.x.toLocaleString()}, Additional Match €${context.parsed.y.toLocaleString()}`;
          },
        },
      },
      legend: {
        display: true,
        position: 'top' as const,
      },
    },
    interaction: {
      intersect: false,
      mode: 'nearest' as const,
    },
  };

  // If no project is selected, don't render the graph
  if (!selectedProject) {
    return null;
  }

  return (
    <Card className="border-0 shadow-sm mt-4">
      <Card.Header className="bg-white border-0">
        <h5 className="mb-0">Donation Impact Analysis for {selectedProject.title}</h5>
        <small className="text-muted">
          How your donation amount would affect matching funds in the current state
        </small>
      </Card.Header>
      <Card.Body>
        <Scatter data={data} options={options} />
        <div className="text-center mt-3">
          <small className="text-muted">
            {getFormulaDescription(config)} vs traditional linear matching
          </small>
        </div>
      </Card.Body>
    </Card>
  );
};

// Helper functions for formula descriptions
function getFormulaName(formulaType: string): string {
  switch (formulaType) {
    case 'standard': return 'Standard QF';
    case 'capped': return 'Capped QF';
    case 'two-tier': return 'Two-Tier QF';
    case 'declining': return 'Declining QF';
    default: return 'Quadratic Funding';
  }
}

function getFormulaDescription(config: SimulationConfig): string {
  switch (config.formulaType) {
    case 'standard':
      return 'Standard quadratic formula showing square of sum of square roots';
    case 'capped':
      return `Capped QF with €${config.formulaParams.cap || 5} contribution limit`;
    case 'two-tier':
      return `Two-tier QF with €${config.formulaParams.threshold || 3} threshold (α1=${config.formulaParams.alpha1 || 0.5}, α2=${config.formulaParams.alpha2 || 0.1})`;
    case 'declining':
      return `Declining marginal QF with power factor β=${config.formulaParams.beta || 0.7}`;
    default:
      return 'Showing actual donations (dots) vs theoretical matching (line)';
  }
}

export default DonationGraph; 